// import { disabledFriday } from '@/configs/general.configs';
import moment from 'moment';
// import store from '../store';
import { entityType } from './enums';
import { QR_SECURITY_CODE } from './constants';

export function getEntityNameByType (type) {
	switch (type) {
	case entityType.doctor: return 'doctor';
	case entityType.center: return 'center';
	case entityType.pharmacy: return 'pharmacy';
	default: return null;
	}	
}

const inFlightRequests = {}

export function requestOnce(key, requestFn) {
	if (inFlightRequests[key]) return inFlightRequests[key]
	const promise = requestFn().finally(() => {
		delete inFlightRequests[key]
	})
	inFlightRequests[key] = promise
	return promise
}

export function isTouchDevice() {
	return (
		window.matchMedia('(pointer: coarse)').matches ||
		('ontouchstart' in window) ||
		(navigator.maxTouchPoints > 0) ||
		(navigator.msMaxTouchPoints > 0)
	);
}

// great-circle distance between two lat/lng points, in meters (haversine formula)
export function getDistanceInMeters(lat1, lon1, lat2, lon2) {
	const EARTH_RADIUS_METERS = 6371000
	const toRad = (deg) => (deg * Math.PI) / 180

	const dLat = toRad(lat2 - lat1)
	const dLon = toRad(lon2 - lon1)

	const a = Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2

	return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function getWeekRange(date) {
	const m = moment(date)

	const start = m.clone().isoWeekday(6) // Saturday
	if (m.isoWeekday() < 6) {
		start.subtract(7, 'days')
	}

	const end = start.clone().add(5, 'days') // Thursday

	return {
		start: start.format('YYYY-MM-DD'),
		end: end.format('YYYY-MM-DD')
	}
}

export function formatNumber(value, locale = 'en-US') {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	const numeric = typeof value === 'number'
		? value
		: Number(String(value).replace(/,/g, ''));

	if (Number.isNaN(numeric)) {
		return value;
	}

	return new Intl.NumberFormat(locale, {
		maximumFractionDigits: 20,
	}).format(numeric);
}

const EASTERN_ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

export function toWesternDigits(value) {
	return String(value).replace(/[٠-٩۰-۹]/g, digit => {
		const easternIndex = EASTERN_ARABIC_DIGITS.indexOf(digit);
		return String(easternIndex !== -1 ? easternIndex : PERSIAN_DIGITS.indexOf(digit));
	});
}

export function parseFormattedNumber(value, options = {}) {
	const { allowDecimal = true } = options;

	if (value === null || value === undefined || value === '') {
		return null;
	}

	const normalized = toWesternDigits(value)
		.replace(/٬/g, '')
		.replace(/٫/g, '.')
		.replace(/,/g, '')
		.replace(/\s/g, '')
		.trim();

	if (!normalized) {
		return null;
	}

	if (!allowDecimal && normalized.includes('.')) {
		return Number.parseInt(normalized, 10);
	}

	const parsed = allowDecimal
		? Number.parseFloat(normalized)
		: Number.parseInt(normalized, 10);

	return Number.isNaN(parsed) ? null : parsed;
}

export const getDateColor = (date, vacations = [], options = {}) => {
	const { vacationDayDetails = [], enableVacations = true } = options;

	if (!enableVacations) {
		return 'success';
	}

	const target = moment(date);
	if (!target.isValid()) {
		return 'success';
	}

	const formattedDate = target.format('YYYY-MM-DD');
	const targetDayOfWeek = target.day();
	const targetDayOfMonth = target.date();
	const targetMonthDay = target.format('MM-DD');

	const isBetweenInclusive = (value, start, end) => {
		if (!start || !end) return true;
		const startMoment = moment(start);
		const endMoment = moment(end);
		if (!startMoment.isValid() || !endMoment.isValid()) return true;
		return moment(value).isBetween(startMoment, endMoment, 'day', '[]');
	};

	const betweenOrdinal = (val, from, to) => {
		if (from <= to) {
			return val >= from && val <= to;
		}
		return val >= from || val <= to;
	};

	const normalizeDate = (d) => {
		if (!d) return null;
		const m = moment(d);
		return m.isValid() ? m.format('YYYY-MM-DD') : null;
	};

	const isVacationDay = Array.isArray(vacationDayDetails) && vacationDayDetails.some((d) => normalizeDate(d) === formattedDate);

	if (isVacationDay) {
		return 'error';
	}

	if (!Array.isArray(vacations) || vacations.length === 0) {
		return 'success';
	}

	const holiday = vacations.some((vacation) => {
		if (!vacation) {
			return false;
		}

		const fromDate = normalizeDate(vacation.fromDate);
		const toDate = normalizeDate(vacation.toDate);
		const startDate = normalizeDate(vacation.startDate);
		const endDate = normalizeDate(vacation.endDate);
		const hasRepeatRange = startDate && endDate && startDate !== '0001-01-01' && endDate !== '0001-01-01';

		if (vacation.repeatType === null || vacation.repeatType === undefined) {
			if (fromDate && toDate) {
				return moment(formattedDate).isBetween(fromDate, toDate, 'day', '[]');
			}
			return false;
		}

		if (vacation.repeatType === 0) {
			if (!fromDate || !toDate) {
				return false;
			}

			const fromDay = moment(fromDate).day();
			const toDay = moment(toDate).day();
			const weeklyMatch = betweenOrdinal(targetDayOfWeek, fromDay, toDay);
			const rangeMatch = hasRepeatRange ? isBetweenInclusive(formattedDate, startDate, endDate) : true;
			return weeklyMatch && rangeMatch;
		}

		if (vacation.repeatType === 1) {
			if (!fromDate || !toDate) {
				return false;
			}

			const fromDay = moment(fromDate).date();
			const toDay = moment(toDate).date();
			const monthlyMatch = betweenOrdinal(targetDayOfMonth, fromDay, toDay);
			const rangeMatch = hasRepeatRange ? isBetweenInclusive(formattedDate, startDate, endDate) : true;
			return monthlyMatch && rangeMatch;
		}

		if (vacation.repeatType === 2) {
			if (!fromDate || !toDate) {
				return false;
			}

			const fromMonthDay = moment(fromDate).format('MM-DD');
			const toMonthDay = moment(toDate).format('MM-DD');
			const yearlyMatch = betweenOrdinal(targetMonthDay, fromMonthDay, toMonthDay);
			const rangeMatch = hasRepeatRange ? isBetweenInclusive(formattedDate, startDate, endDate) : true;
			return yearlyMatch && rangeMatch;
		}

		return false;
	});

	return holiday ? 'error' : 'success';
};

function base64ToBytes(base64) {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

/*
	Client-side fallback for /entities/qrCode/decode, used when offline. Same scheme the
	backend uses to encode an entity's id/name/typeId/regionId into a QR code:
	AES-256-CBC, key = raw bytes of QR_SECURITY_CODE, IV = the ciphertext's first 16 bytes.
*/
export function decryptQrData(qrCodeData) {
	// atob يرمي استثناءً متزامناً على رمز تالف، والملتقط في الشاشات ينتظر رفضاً لا رمية
	return Promise.resolve().then(() => {
		const keyBytes = base64ToBytes(QR_SECURITY_CODE);
		const payload = base64ToBytes(qrCodeData);
		const iv = payload.slice(0, 16);
		const ciphertext = payload.slice(16);

		return window.crypto.subtle.importKey('raw', keyBytes, 'AES-CBC', false, ['decrypt'])
			.then(key => window.crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ciphertext))
			.then(decrypted => JSON.parse(new TextDecoder().decode(decrypted)));
	});
}
/*
	يوحّد شكل الجهة الراجعة من فك تشفير الـ QR: قد تصل مباشرة أو ملفوفة داخل data/entity،
	وبأسماء حقول مختلفة (Id / entityId، typeId / entityTypeId)، بينما تتوقّع الشاشات دائماً
	{ id, name, typeId, regionId }
*/
export function normalizeQrEntity(payload) {
	const raw = Array.isArray(payload) ? payload[0] : payload;
	if (!raw || typeof raw !== 'object') return null;

	const entity = raw.entity || raw.data || raw;
	const pick = keys => keys
		.map(key => entity[key])
		.find(value => value !== undefined && value !== null && value !== '') || null;

	const id = pick(['id', 'Id', 'entityId', 'EntityId']);
	if (!id) return null;

	return {
		...entity,
		id,
		name: pick(['name', 'Name', 'entityName', 'EntityName']),
		typeId: pick(['typeId', 'TypeId', 'entityTypeId', 'EntityTypeId']),
		regionId: pick(['regionId', 'RegionId'])
	};
}

/*
	يصغّر الصورة قبل رفعها: صورة الهاتف الخام قد تصل إلى 8MB بينما يكفي عرض 1280px
	نرجّع الملف الأصلي كما هو إذا لم يكن صورة، أو كان أصغر من الحد، أو فشل التحويل
*/
export function downscaleImage(file, maxSize = 1280, quality = 0.8) {
	if (!file || !file.type?.startsWith('image/')) return Promise.resolve(file);

	return new Promise(resolve => {
		const objectUrl = URL.createObjectURL(file);
		const image = new Image();

		image.onload = () => {
			URL.revokeObjectURL(objectUrl);

			const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
			if (scale === 1) return resolve(file);

			const canvas = document.createElement('canvas');
			canvas.width = Math.round(image.width * scale);
			canvas.height = Math.round(image.height * scale);
			canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);

			canvas.toBlob(blob => {
				if (!blob) return resolve(file);
				const name = `${file.name.replace(/\.[^.]+$/, '')}.jpg`;
				resolve(new File([blob], name, { type: 'image/jpeg' }));
			}, 'image/jpeg', quality);
		};

		image.onerror = () => {
			URL.revokeObjectURL(objectUrl);
			resolve(file);
		};

		image.src = objectUrl;
	});
}

// a visit stays open for `lockVisitAfterDuration` hours back from the server's own clock.
// compared at day granularity, and never into the future. gates adding a visit on a day
// as well as editing or deleting one already recorded on it
export function isWithinVisitLockWindow (date, serverTime, lockVisitAfterDuration) {
	if (!date) return false;

	// the server clock is the reference, but a page reached before it loads falls back
	// to the device's own rather than locking everything away
	const now = serverTime ? moment(serverTime) : moment();
	const selected = moment(date);
	const oldestAllowed = moment(now).subtract(lockVisitAfterDuration, 'hour');

	return selected.isSameOrAfter(oldestAllowed, 'day') && selected.isSameOrBefore(now, 'day');
}

// الحقل يصل أحياناً كمصفوفة أسماء وأحياناً كسلسلة واحدة مفصولة بفواصل، فيُطبَّع
// إلى قائمة أسماء نظيفة قبل مطابقتها بقائمة المواد
export function toMaterialNamesList(value) {
	const names = Array.isArray(value) ? value : String(value ?? '').split(',');

	return names.map(name => String(name ?? '').trim()).filter(Boolean);
}
